import * as React from "react";
import { Check, Copy, Info } from "lucide-react";
import { openBookingWidget } from "@/components/BookingButton";
import { logOfferEvent } from "@/lib/offers.functions";
import { useServerFn } from "@tanstack/react-start";
import type { Offer } from "./offer-data";
import { toast } from "sonner";

export function OfferCard({
  offer,
  email,
  beforeBookingOpen,
}: {
  offer: Offer;
  email?: string;
  beforeBookingOpen?: () => void | Promise<void>;
}) {
  const log = useServerFn(logOfferEvent);
  const [copied, setCopied] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);

  const fireEvent = (
    eventName: "offer_copy_code" | "offer_book_click",
  ) => {
    log({
      data: { eventName, offerCode: offer.code, email: email ?? null },
    }).catch(() => {});
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(offer.code);
      setCopied(true);
      fireEvent("offer_copy_code");
      toast.success(`${offer.code} copied to clipboard`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy automatically. Long-press to copy.");
    }
  };

  return (
    <div className="flex flex-col border border-foreground/10 rounded-2xl bg-foreground/[0.02] overflow-hidden">
      <div className="relative aspect-[16/10] w-full overflow-hidden">
        <img
          src={offer.image}
          alt={offer.title}
          className="h-full w-full object-cover"
          loading="lazy"
        />
        <div className="absolute top-3 left-3 text-[9px] tracking-[0.35em] uppercase text-gold/95 bg-background/70 backdrop-blur px-2.5 py-1 rounded-full">
          {offer.badge}
        </div>
      </div>

      <div className="p-6 md:p-7 flex flex-col">
        <h3 className="font-serif text-2xl md:text-[1.6rem] text-foreground leading-tight">
          {offer.title}
        </h3>
        <p className="text-foreground/70 italic font-light mt-1.5 text-sm md:text-base">
          {offer.subtitle}
        </p>
        {/* Bonus block — always visible */}
        <div className="mt-5 rounded-lg border border-gold/25 bg-gold/[0.05] p-4">
          <p className="text-sm text-gold/95 font-medium leading-snug mb-2">
            {offer.teaser}
          </p>
          <div className="text-[9px] tracking-[0.3em] uppercase text-gold/80 mb-2">
            Bonus — {offer.bonusValue} total value
          </div>
          {offer.bonusDetails.length > 0 ? (
            <ul className="space-y-1.5 text-sm text-foreground/85 font-light">
              {offer.bonusDetails.map((item) => (
                <li key={item} className="flex gap-2 leading-snug">
                  <span className="text-gold mt-0.5">·</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-foreground/85 font-light leading-snug">
              {offer.bonusCopy}
            </p>
          )}
        </div>

        {/* Promo code — always visible */}
        <div className="mt-4 flex items-center gap-2">
          <div className="flex-1 flex items-center justify-between border border-foreground/15 rounded-md bg-background px-3 py-2.5">
            <span className="text-[9px] tracking-[0.3em] uppercase text-foreground/55">
              Code
            </span>
            <span className="font-mono text-base tracking-[0.2em] text-foreground">
              {offer.code}
            </span>
          </div>
          <button
            type="button"
            onClick={onCopy}
            className="inline-flex items-center gap-1.5 border border-foreground/15 rounded-md px-3 py-2.5 text-xs uppercase tracking-[0.2em] text-foreground/80 hover:border-gold/50 hover:text-foreground transition"
          >
            {copied ? (
              <>
                <Check className="h-3.5 w-3.5" /> Copied
              </>
            ) : (
              <>
                <Copy className="h-3.5 w-3.5" /> Copy
              </>
            )}
          </button>
        </div>
        <div className="flex items-start gap-1.5 mt-2">
          <Info className="h-4 w-4 text-gold shrink-0 mt-0.5" />
          <p className="text-[13px] text-foreground font-semibold leading-snug">
            Paste code on first booking screen before selecting dates
          </p>
        </div>

        {/* Primary CTA */}
        <div className="mt-4">
          <button
            type="button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              fireEvent("offer_book_click");
              openBookingWidget(beforeBookingOpen).catch(() => {});
            }}
            className="w-full font-serif italic text-base text-background bg-gold/95 px-6 py-3 rounded-full tracking-wide hover:bg-gold transition"
          >
            {offer.ctaLabel}
          </button>
          <p className="text-[11px] text-foreground/55 font-light mt-2 text-center">
            {offer.fineprint}
          </p>
        </div>

        {/* See details toggle */}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-4 text-[11px] uppercase tracking-[0.3em] text-foreground/55 hover:text-foreground/80 transition self-center"
        >
          {expanded ? "Hide details" : "See details"}
        </button>

        {expanded && (
          <div className="mt-4 pt-4 border-t border-foreground/10 space-y-4">
            <p className="text-foreground/75 font-light text-sm leading-relaxed">
              {offer.body}
            </p>
            <p className="text-xs text-foreground/60 italic font-light leading-relaxed">
              {offer.scarcity}
            </p>
            <div>
              <div className="text-[9px] tracking-[0.3em] uppercase text-foreground/55 mb-1">
                Best for
              </div>
              <p className="text-xs text-foreground/70 font-light leading-relaxed">
                {offer.bestFor}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
